import Vue from 'vue';
import VueRouter from 'vue-router';
import adminRoutes from './admin';
import teacherRoutes from './teacher';
import parentRoutes from './parent';
import studentRoutes from './student';
import questionCreatorRoutes from './question-creator';
import questionViewerRoutes from './question-viewer';

Vue.use(VueRouter);

// 基础路由（不带学校编码）
const baseRoutes = [
  {
    path: '/',
    redirect: '/login'
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/Login.vue')
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('@/views/auth/Register.vue')
  },
  {
    path: '/test',
    name: 'TestPage',
    component: () => import('@/views/TestPage.vue')
  },
  {
    path: '/latex-demo',
    name: 'LatexDemo',
    component: () => import('@/components/common/LatexDemo.vue')
  },
  {
    path: '/campus-selection',
    name: 'CampusSelection',
    component: () => import('@/views/auth/CampusSelection.vue'),
    meta: { requiresAuth: true }
  },
  ...adminRoutes,
  ...teacherRoutes,
  ...parentRoutes,
  ...studentRoutes,
  ...questionCreatorRoutes,
  ...questionViewerRoutes,
  {
    path: '*',
    redirect: '/404'
  },
  {
    path: '/404',
    name: '404',
    component: () => import('@/views/error/404.vue')
  }
];

// 带学校编码的路由
const routes = [
  // 根路径重定向到404，因为需要带学校编码访问
  {
    path: '/',
    redirect: '/404'
  },
  // 测试页面直接访问
  {
    path: '/test',
    component: () => import('@/views/TestPage.vue')
  },
  // LaTeX演示页面直接访问
  {
    path: '/latex-demo',
    component: () => import('@/components/common/LatexDemo.vue')
  },
  // 学校编码路由
  {
    path: '/:schoolCode',
    component: {
      render: (c) => c('router-view')
    },
    children: [
      {
        path: '',
        redirect: to => `/${to.params.schoolCode}/login`
      },
      {
        path: 'login',
        component: () => import('@/views/auth/Login.vue'),
        meta: { schoolRequired: true }
      },
      {
        path: 'test',
        component: () => import('@/views/TestPage.vue'),
        meta: { schoolRequired: true }
      },
      {
        path: 'register',
        component: () => import('@/views/auth/Register.vue'),
        meta: { schoolRequired: true }
      },
      {
        path: 'campus-selection',
        component: () => import('@/views/auth/CampusSelection.vue'),
        meta: { requiresAuth: true, schoolRequired: true }
      },
      // 校区编码嵌套路由 
      {
        path: ':campusCode',
        component: {
          render: (c) => c('router-view')
        },
        children: [
          // 将管理员路由添加为校区子路由
          ...adminRoutes.map(route => ({
            ...route,
            path: route.path.replace(/^\/admin/, 'admin'), // 移除前导斜杠
            meta: { ...route.meta, schoolRequired: true, campusRequired: true }
          })),
          // 将教师路由添加为校区子路由
          ...teacherRoutes.map(route => ({
            ...route,
            path: route.path.replace(/^\/teacher/, 'teacher'), // 移除前导斜杠
            meta: { ...route.meta, schoolRequired: true, campusRequired: true }
          })),
          // 将家长路由添加为校区子路由
          ...parentRoutes.map(route => ({
            ...route,
            path: route.path.replace(/^\/parent/, 'parent'), // 移除前导斜杠
            meta: { ...route.meta, schoolRequired: true, campusRequired: true }
          })),
          // 将学生路由添加为校区子路由
          ...studentRoutes.map(route => ({
            ...route,
            path: route.path.replace(/^\/student/, 'student'), // 移除前导斜杠
            meta: { ...route.meta, schoolRequired: true, campusRequired: true }
          })),
          // 将录题人员路由添加为校区子路由
          ...questionCreatorRoutes.map(route => ({
            ...route,
            path: route.path.replace(/^\/question-creator/, 'question-creator'), // 移除前导斜杠
            meta: { ...route.meta, schoolRequired: true, campusRequired: true }
          })),
          // 将查看人员路由添加为校区子路由
          ...questionViewerRoutes.map(route => ({
            ...route,
            path: route.path.replace(/^\/question-viewer/, 'question-viewer'), // 移除前导斜杠
            meta: { ...route.meta, schoolRequired: true, campusRequired: true }
          }))
        ]
      },
      // 为了向后兼容，保留不带校区代码的路由
      // 将管理员路由添加为子路由
      ...adminRoutes.map(route => ({
        ...route,
        path: route.path.replace(/^\/admin/, 'admin'), // 移除前导斜杠
        meta: { ...route.meta, schoolRequired: true }
      })),
      // 将教师路由添加为子路由
      ...teacherRoutes.map(route => ({
        ...route,
        path: route.path.replace(/^\/teacher/, 'teacher'), // 移除前导斜杠
        meta: { ...route.meta, schoolRequired: true }
      })),
      // 将家长路由添加为子路由
      ...parentRoutes.map(route => ({
        ...route,
        path: route.path.replace(/^\/parent/, 'parent'), // 移除前导斜杠
        meta: { ...route.meta, schoolRequired: true }
      })),
      // 将学生路由添加为子路由
      ...studentRoutes.map(route => ({
        ...route,
        path: route.path.replace(/^\/student/, 'student'), // 移除前导斜杠
        meta: { ...route.meta, schoolRequired: true }
      })),
      // 将录题人员路由添加为子路由
      ...questionCreatorRoutes.map(route => ({
        ...route,
        path: route.path.replace(/^\/question-creator/, 'question-creator'), // 移除前导斜杠
        meta: { ...route.meta, schoolRequired: true }
      })),
      // 将查看人员路由添加为子路由
      ...questionViewerRoutes.map(route => ({
        ...route,
        path: route.path.replace(/^\/question-viewer/, 'question-viewer'), // 移除前导斜杠
        meta: { ...route.meta, schoolRequired: true }
      })),
      {
        path: 'home',
        name: 'StudentHome',
        component: () => import('@/views/student/Home'),
        meta: { title: '首页', icon: 'el-icon-s-home' }
      },
      {
        path: 'performance',
        name: 'StudentPerformance',
        component: () => import('@/views/student/Performance'),
        meta: { title: '表现记录', icon: 'el-icon-s-opportunity' }
      }
    ]
  },
  // 404页面
  {
    path: '/404',
    name: '404',
    component: () => import('@/views/error/404.vue')
  },
  // 通配路由，匹配所有未定义的路由
  {
    path: '*',
    redirect: '/404'
  }
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
});

// 全局路由守卫
router.beforeEach((to, from, next) => {
  const isLoggedIn = localStorage.getItem('token');
  const schoolCode = to.params.schoolCode;
  const campusCode = to.params.campusCode;
  
  console.log('路由守卫检查:', {
    路径: to.path,
    学校代码: schoolCode,
    校区代码: campusCode,
    是否登录: !!isLoggedIn,
    目标路由: to.fullPath
  });

  // 在开发模式下，如果没有登录且访问需要权限的页面，自动模拟登录
  if (process.env.NODE_ENV === 'development' && 
      !isLoggedIn && 
      to.matched.some(record => record.meta.requiresAuth)) {
    
    console.log('开发模式：自动添加测试凭据');
    // 设置一个模拟的token
    localStorage.setItem('token', 'dev-token');
    localStorage.setItem('tokenType', 'Bearer');
    localStorage.setItem('userRole', to.path.includes('/student') ? 'student' : 
                          to.path.includes('/teacher') ? 'teacher' : 
                          to.path.includes('/parent') ? 'parent' : 'admin');
    
    // 设置基本用户信息
    const userInfo = {
      username: '测试用户',
      role: localStorage.getItem('userRole')
    };
    localStorage.setItem('userInfo', JSON.stringify(userInfo));
    
    next();
    return;
  }

  // 检查是否需要权限验证
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!isLoggedIn) {
      // 如果需要登录但未登录，重定向到登录页
      if (schoolCode) {
        next({
          path: `/${schoolCode}/login`,
          query: { redirect: to.fullPath }
        });
      } else {
        next({
          path: '/404'
        });
      }
    } else {
      next();
    }
  } 
  // 检查是否需要学校编码
  else if (to.matched.some(record => record.meta.schoolRequired) && !schoolCode) {
    next('/404');
  } 
  // 检查是否需要校区编码
  else if (to.matched.some(record => record.meta.campusRequired) && !campusCode) {
    // 如果需要校区编码但没有，重定向到校区选择页面
    if (schoolCode) {
      next({
        path: `/${schoolCode}/campus-selection`
      });
    } else {
      next('/404');
    }
  }
  // 检查角色路径，如admin、teacher等是否被错误地当作校区代码
  else if (campusCode && ['admin', 'teacher', 'parent', 'student'].includes(campusCode)) {
    // 如果URL格式是 /:schoolCode/:role 而不是 /:schoolCode/:campusCode/:role
    // 我们认为这是一个没有校区代码的路径，不需要重定向
    next();
  }
  else {
    next();
  }
  
  // 保存当前学校编码到localStorage
  if (schoolCode) {
    localStorage.setItem('schoolCode', schoolCode);
  }
  
  // 保存当前校区编码到localStorage
  if (campusCode && !['admin', 'teacher', 'parent', 'student'].includes(campusCode)) {
    // 检查是否有完整的校区信息
    const currentCampus = localStorage.getItem('currentCampus');
    if (!currentCampus || !JSON.parse(currentCampus).code) {
      // 如果没有完整信息，至少保存校区代码
      localStorage.setItem('currentCampus', JSON.stringify({
        code: campusCode
      }));
    }
  }
});

export default router; 